import type { LookupParams } from '@/lib/bootstrap';
import type { BetlensResult } from '@/types';

/**
 * A saved snapshot of one diagnostic run.
 *
 * A bet's data is not frozen — retries land, ClickHouse catches up, traces get
 * archived. A snapshot captures the WHOLE result exactly as it looked at
 * `takenAt`, so support can attach it to a ticket or reopen it later even after
 * the live data has moved on. `input` is kept so the same bet can be re-pulled.
 */
export interface BetSnapshot {
  _betlens: 'snapshot';
  version: 1;
  takenAt: string;
  input: LookupParams | null;
  result: BetlensResult;
}

export function buildSnapshot(
  input: LookupParams | null,
  result: BetlensResult,
): BetSnapshot {
  return { _betlens: 'snapshot', version: 1, takenAt: new Date().toISOString(), input, result };
}

const safe = (s: string): string => s.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 48);

/** e.g. betlens-bet-3207-2026-07-17T17-40-05.json — sortable, ticket-friendly. */
export function snapshotFilename(result: BetlensResult): string {
  const r = result.bundle.resolved;
  const id = r.bet_id || r.cycle_uniq_id || 'bet';
  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `betlens-bet-${safe(id)}-${ts}.json`;
}

/** Download the snapshot as a JSON file. No network — purely client-side. */
export function downloadJson(input: LookupParams | null, result: BetlensResult): void {
  const snapshot = buildSnapshot(input, result);
  const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = snapshotFilename(result);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
